/**
 * Wire types for the project settings dialog: the vocabularies, and who may do
 * what.
 *
 * Separate from [BoardState] rather than bolted onto it, and the split is the
 * point. The board sends every reader the vocabulary *names* — a card cannot
 * render "Bug" without them. This sends an admin the vocabulary's **shape**: how
 * many issues hold each row, which row is the magic closing column, and a
 * directory of every account on the instance with its grants. None of that is
 * needed to draw a board, and a signed-out visitor reading a public project has
 * no business receiving a list of everyone who has ever signed in.
 *
 * So this is its own request, refused outright to anyone who is not an admin —
 * not "sent to everyone and hidden by the bundle", which is the failure
 * BoardRoutes' preamble names as the one that gets forgotten.
 *
 * @see BoardState
 * @see se.soderbjorn.lunicle.clientserver.ApiRoutes.projectSettings
 */
package se.soderbjorn.lunicle.clientserver

import kotlinx.serialization.Serializable

/**
 * Which of a project's six vocabularies a request is about.
 *
 * One enum and one set of routes rather than six near-identical route families,
 * because they differ in very little — what deleting one costs, and whether a
 * project can be without it — and both of those are decisions the *server*
 * makes. Six copies of "add a row, rename a row, delete a row" would be six
 * places to forget the last-status rule.
 *
 * Every kind is ordered. That used to be the other axis they differed on, until
 * labels and components gained a `position` too (see LNL-28); a sprint arrived
 * already carrying one, for a third reason again — not because the order *is*
 * the data, but because planning wants to slot a sprint between two that already
 * exist. See Sprints.sq's `position`.
 *
 * The four properties below are facts about the *schema*, and they live here
 * rather than on the server because both sides need the same answers and there
 * must not be two of them. The server refuses a delete that [restrictsOnUse]
 * covers; the client, holding the same property, declines to offer the button.
 * That is the affordance relationship this codebase runs on — one rule, enforced
 * on one side and rendered on the other — and it only works while it is literally
 * one expression. Two copies would drift, and the drift would show up as a button
 * that is offered and then refused.
 *
 * @property key what appears in the URL. Explicit rather than `name.lowercase()`:
 *   these strings are wire format, so a rename of the constant must not silently
 *   move a route. Changing one is a client-and-server change, which is precisely
 *   what having to edit this line makes you notice.
 * @property noun what to call one of these in a sentence — "There is already a
 *   label called…". Identical to [key] today, and deliberately not the same
 *   property: one is prose an admin reads and the other is a URL segment, and the
 *   day a kind needs a two-word name is the day conflating them would put a space
 *   in a route.
 * @property plural the same word, more than one of it. Carried rather than
 *   computed, because `noun + "s"` produces "statuss" and "prioritys" — which is
 *   not a hypothetical: it shipped as far as a manual pass, in the sentence
 *   refusing a bad reorder. English plurals are data, not a rule.
 */
@Serializable
enum class VocabularyKind(val key: String, val noun: String, val plural: String) {
    LABEL("label", "label", "labels"),
    COMPONENT("component", "component", "components"),
    STATUS("status", "status", "statuses"),
    PRIORITY("priority", "priority", "priorities"),
    RESOLUTION("resolution", "resolution", "resolutions"),

    /**
     * Timeboxes. The one kind a project may legitimately have none of forever —
     * see Sprints.sq. Everything add/rename/delete/reorder about a sprint is
     * this enum's machinery; activating and completing one are their own routes,
     * because neither is a thing you can do to a label.
     */
    SPRINT("sprint", "sprint", "sprints"),

    /**
     * Release versions (LNL-134). A project may have none forever, like sprints —
     * every add/rename/delete/reorder is this enum's machinery. An issue points at
     * one twice (its planned version and its fixed version), but that is a fact
     * about `issues`, not about the version, so nothing extra rides on the kind.
     */
    VERSION("version", "version", "versions"),

    /**
     * The ways two issues in this project can be said to be related — "Blocked by",
     * "Duplicate of", "Related to" (LNL-215).
     *
     * The richest row of any kind here: two names plus a flag, where a status has one
     * name plus a flag and a label has just the name. It is still this enum's
     * machinery rather than a family of its own, because add / rename / reorder /
     * delete is exactly what an administrator does to it and [VocabularyRow] already
     * carries per-kind extras for statuses and resolutions.
     *
     * A project may legitimately have none — deleting the last one simply means
     * nobody here links issues — so it is not [isLoadBearing]. Every project made or
     * migrated since LNL-215 starts with three; see IssueRelationKinds.sq.
     */
    RELATION_KIND("relation-kind", "relation kind", "relation kinds");

    /**
     * Whether the database refuses to delete one of these while an issue holds it.
     *
     * The three that `issues` points at directly — status, priority, resolution —
     * are composite-keyed with no ON DELETE clause, so SQLite's default RESTRICT
     * fires. Labels and components are reached through `issue_labels` /
     * `issue_components`, whose keys are ON DELETE CASCADE: deleting one of those
     * unlabels the issues and leaves them standing, which is a consequence to warn
     * about rather than a reason to refuse.
     *
     * Sprints are pointed at directly by `issues` like the first three, and are
     * still false here, because their key is `ON DELETE SET NULL`: deleting a
     * sprint un-schedules its issues rather than being refused. That is the
     * whole reason a sprint could join this enum at all — see Sprints.sq for why
     * the reference could not be composite, which is what makes the other three
     * restrict.
     *
     * This mirrors the schema. If an ON DELETE clause ever changes, this is the
     * line that changes with it — and both the server's refusal and the client's
     * disabled button follow, because both read this.
     */
    val restrictsOnUse: Boolean
        get() = when (this) {
            STATUS, PRIORITY, RESOLUTION -> true
            // VERSION joins sprints on the false side, and for the same reason:
            // both issue references are ON DELETE SET NULL, so deleting a version
            // releases the issues that named it rather than being refused. See
            // Versions.sq.
            // RELATION_KIND joins the false side from a third direction again: its
            // relations are ON DELETE CASCADE, so deleting a kind takes the links
            // that used it rather than being refused. That is not the SET NULL a
            // version takes — a relation row without its kind would be two issue ids
            // and no statement about them, where an issue without a fixed version is
            // an ordinary issue. The count is still shown before the fact; see
            // IssueRelationKinds.sq's `delete`.
            LABEL, COMPONENT, SPRINT, VERSION, RELATION_KIND -> false
        }

    /**
     * Whether a project stops working without at least one of these.
     *
     * `IssueRepository.createDraft` reads the leftmost status and the middle
     * priority, and errors if either is missing — so a project with none of either
     * cannot take an issue, and cannot be fixed by filing one about it. Deleting
     * the last one is refused for that reason and no other.
     *
     * Nothing else is load-bearing that way. A project with no labels, no
     * components or no resolutions is unremarkable, and every one of them can be
     * added back from the dialog that emptied it. The rule is not "never run out",
     * it is "never become unrepairable".
     *
     * Sprints go further than unremarkable: having none is the *default*, and the
     * state every project that does not want timeboxes stays in permanently. A
     * rule about running out of them would be backwards.
     */
    val isLoadBearing: Boolean
        get() = this == STATUS || this == PRIORITY
}

/**
 * One row of one vocabulary, as the settings dialog needs it.
 *
 * Richer than the [VocabularyItem] the board gets, and deliberately so — see this
 * file's preamble.
 *
 * @property position where it sits in the order, 0 first. Every kind has one:
 *   statuses are board columns, priorities are a scale, and labels and
 *   components are lists somebody curated — alphabetical was never a decision
 *   anyone made, only what you got when nobody had. See Labels.sq.
 * @property requiresResolution whether landing in this column demands a
 *   resolution. Only ever meaningful for a status; see Statuses.sq.
 * @property usageCount how many issues hold this row *right now*. What it means
 *   depends on the kind, and the client must not guess: for a status or a
 *   priority a non-zero count means the delete will be refused, and for a label it
 *   means that many issues will be unlabelled. [VocabularyKind] does not carry
 *   that rule either — [ProjectSettingsState] is rendered by a view model that
 *   phrases both sentences, and the server refuses regardless of what the client
 *   believed.
 *
 *   Published issues only. A draft holds a status like any other row, and counting
 *   one used to be the point — it is a draft that makes a delete fail — but a
 *   draft lands in the leftmost column and is never collected, so that made the
 *   first column of a long-lived project permanently undeletable over issues its
 *   admin could not see (LNL-183). The server clears the drafts out of a deleted
 *   row's way instead; see Issues.sq's usageByStatus.
 */
@Serializable
data class VocabularyEntry(
    val id: Long,
    val name: String,
    val position: Int = 0,
    val requiresResolution: Boolean = false,
    /**
     * Whether this resolution means the work was actually done (LNL-134). Only ever
     * meaningful for a resolution — the mirror of [requiresResolution]; see
     * Resolutions.sq. It is what the Structure tab renders as a checkbox and what
     * the resolution dialog reads to decide whether a fixed version is required.
     */
    val isDone: Boolean = false,
    val usageCount: Int = 0,
    /**
     * When this sprint was completed, or null because it has not been (LNL-196).
     *
     * **Sprints only**, like [requiresResolution] is statuses only — a version or a
     * label leaves it null forever. It rides here rather than on a sprint-shaped wire
     * type of its own because the Sprints section renders the same rows every other
     * vocabulary does, plus a date and one action; a parallel type would be
     * [VocabularyEntry] with two fields added and every other screen having to know
     * which of the two it was handed.
     *
     * Note that the server's `VocabularyRow` deliberately does *not* carry this — see
     * `VocabularyRepository`'s `SprintRecord.toRow`. The completion instant is joined
     * in where the settings response is assembled, which keeps the row shape "a named
     * thing you can rename, reorder and delete" and out of the completion business.
     */
    val completedAt: Long? = null,
    /**
     * How many issues in this sprint are **not** in a closing column (LNL-196).
     *
     * Sprints only, for [completedAt]'s reason, and null-equivalent (0) everywhere
     * else. It is what the completion confirmation counts: "3 issues in Sprint 4 are
     * not finished. Where should they go?" — a question that has to be asked, and one
     * the browser cannot answer for itself, because "unfinished" means "not in a
     * status that requires a resolution" and the settings pane does not hold the
     * board's issues.
     */
    val unfinishedCount: Int = 0,
    /**
     * The **to**-side label of a relation kind — "Blocks" beside a [name] of "Blocked
     * by" — or null because the kind reads the same in both directions (LNL-215).
     *
     * **Relation kinds only**, like [completedAt] is sprints only, and null-forever
     * for every other kind. Null IS the encoding of symmetry: the row's "same in both
     * directions" checkbox clears and disables this field, and there is no separate
     * flag that could disagree with it. See IssueRelationKinds.sq's inverse_name.
     */
    val inverseName: String? = null,
    /**
     * Whether an issue on the *from* side of one of these counts as blocked, and is
     * dimmed and chipped on the board (LNL-215). Relation kinds only — the same shape
     * as a status's [requiresResolution], and rendered as the same kind of checkbox.
     */
    val marksBlocked: Boolean = false,
    /**
     * Whether an issue in this column has stopped blocking the issues that wait on
     * it. **Statuses only**, like [requiresResolution], which implies it — a closing
     * column unblocks whether or not this is set. See Statuses.sq's unblocks.
     */
    val unblocks: Boolean = false,
)

/**
 * The one rung key both halves of the wire have to know by name.
 *
 * The rest of the ladder the client renders blindly — a key it does not recognise is
 * just a row it draws from [RungOption.description] without knowing what it means,
 * which is the point of sending descriptions at all. This one is the exception
 * because it is a *default*: [ProjectSummary.roleKey] falls back to it, so a summary
 * from a server that said nothing about a rung claims the bottom of the ladder
 * rather than the top.
 *
 * One constant, deliberately (LNL-194). This held a key per privilege while there
 * were seven independent ones to name; the rungs are the server's
 * [se.soderbjorn.lunicle.ProjectRole] now and travel as [RungOption], so restating
 * them here would be a second list to keep in step with the first.
 */
object RoleKeys {
    const val VIEWER: String = "viewer"
}

/**
 * The three sources a project's GitHub token can have, as the strings both halves
 * of the wire name it by (LNL-107).
 *
 * Hoisted here for [RoleKeys]' reason: the server's `TokenSource` maps to and from
 * these, the client's radio renders them, and [ProjectUpdate.githubTokenMode]
 * carries one — so the one place they are spelled has to be shared, or a rename on
 * one side becomes a mode nobody recognises on the other.
 */
object TokenModes {
    /** No token — the commit counts go unavailable, everything else answers. */
    const val NONE: String = "none"

    /** The token is the value of a named environment variable, resolved at read time. */
    const val ENV: String = "env"

    /** The token is stored literally on the project. Write-only on the wire. */
    const val LITERAL: String = "literal"
}

// `RoleDescription` stood here — one row of the old tick-box table, a key and the
// sentence shown under its checkbox. It is gone (LNL-194) along with the table: a
// person holds one rung now, and the rung vocabulary travels as [RungOption], which
// carries a label and a per-caller "may you hand this out" beside the sentence.
//
// `ProjectMember` stood here too — a row per account on the instance with the roles it
// held, which is what the old privileges table rendered. It is gone (LNL-194),
// replaced by [PersonRow]: the Access list shows the *exceptions*, not a directory of
// everybody who has ever signed in beside a mostly-empty rung.

/**
 * Everything the settings dialog needs, in one round-trip.
 *
 * One state rather than seven endpoints, for [BoardState]'s reason: the dialog
 * cannot render half of this, and seven requests would be seven chances to paint
 * a dialog that is missing a section.
 *
 * @property canMutateProject whether the caller may write the admin half — the
 *   vocabularies, the sprints and the grants. True for an instance administrator and
 *   for a project administrator *of this project* (LNL-37); it used to mean the
 *   former alone. For everyone else the server sends this false **and omits those
 *   sections entirely**: the dialog is openable by everyone (the issue's "what is
 *   shown depends on if they are an admin"), so this is not "always true in
 *   practice". What they still receive is the notification fields below, which are
 *   theirs to change. An affordance either way — every write route re-derives the
 *   answer from the session.
 * @property sections which sections of this project the caller has, and [access] who
 *   the project admits. See [ProjectAccessState], which replaced the privileges table.
 * @property notifyOnNewIssue whether the caller has asked to be e-mailed when a
 *   new issue is created in this project. Every signed-in reader gets this,
 *   administrator or not; it is the one thing anyone else can change here.
 * @property canReceiveEmailNotifications whether the caller has an address at all.
 *   The toggle is hidden without it, for [IssueDetail.canReceiveEmailNotifications]'s
 *   reason. Computed from the caller's own record server-side.
 */
@Serializable
data class ProjectSettingsState(
    val labels: List<VocabularyEntry> = emptyList(),
    val components: List<VocabularyEntry> = emptyList(),
    val statuses: List<VocabularyEntry> = emptyList(),
    val priorities: List<VocabularyEntry> = emptyList(),
    val resolutions: List<VocabularyEntry> = emptyList(),
    /**
     * The project's sprints, oldest-planned first. Empty for every project that
     * has never made one, which is the default and stays the default — the
     * dialog renders the section regardless, because an empty section with an
     * add field is how you make the first one.
     */
    val sprints: List<VocabularyEntry> = emptyList(),
    /**
     * The project's release versions, in the order the admin arranged (LNL-134).
     * Empty for every project that has never made one — the default, and the
     * dialog renders the section regardless so an empty section with an add field
     * is how you make the first one, exactly like [sprints].
     */
    val versions: List<VocabularyEntry> = emptyList(),
    /**
     * The project's relation kinds, in the order the admin arranged (LNL-215).
     * Rendered in the **Structure** section beside statuses, priorities, resolutions,
     * labels and components — it is vocabulary about what an issue *is*, not about
     * when work happens, so it belongs with those rather than with sprints and
     * versions.
     */
    val relationKinds: List<VocabularyEntry> = emptyList(),
    /**
     * Whether this project estimates, and in what unit — one of [EstimateMode]'s keys
     * (LNL-215). Sent to every caller who reaches this state for [showIssueAuthor]'s
     * reason (the issue window reads it to decide what to offer), and editable only in
     * the admin half. Defaults to `none`, which renders nothing anywhere.
     */
    val estimateMode: String = "none",
    val canMutateProject: Boolean = false,
    /**
     * Whether the caller may shape the sprints and the versions — **Maintainer and
     * above** (LNL-196).
     *
     * A second, lower gate beside [canMutateProject], and the two are different lines
     * for the reason [canConfigureRepository] is a third: scheduling work that already
     * exists is a maintainer's, and what the board *is* is an administrator's. The
     * server has drawn that line since LNL-191 (see `VocabularyKind.minimumRole`); this
     * is the flag that lets the two sections render it, rather than a Maintainer
     * reaching Sprints and finding an empty pane.
     *
     * [sprints] and [versions] travel from Maintainer up for the same reason. They are
     * not secret — the board sends every reader the names, because a card cannot draw a
     * sprint scope without them — so the narrowing here is about who is offered a
     * section, not about what is withheld.
     */
    val canMutateProjectPlanning: Boolean = false,
    /**
     * Why the Sprints and Versions lists are read-only, or null when they are not.
     *
     * Present exactly when [canMutateProjectPlanning] is false and the caller was sent
     * the sections anyway, so the pane can grey its controls with the sentence beside
     * them rather than hiding them — this file's standing rule. See
     * [ProjectAccessState]'s preamble on why a vanished control reads as a bug.
     */
    val planningReadOnlyReason: String? = null,
    /**
     * The sections this caller has on this project, in rail order (LNL-194).
     *
     * Decided by the server from the rung, never by the rail — see [ProjectSection].
     * Never empty: everybody who can see a project at all gets at least
     * [ProjectSectionKeys.ACCESS], which for a Viewer is the "Your access" statement.
     */
    val sections: List<ProjectSection> = emptyList(),
    /**
     * Who this project admits, or null for a caller below Maintainer.
     *
     * Omitted rather than sent-and-flagged, on this file's founding principle: the
     * person rows carry addresses, and somebody who merely reads a board has no
     * business receiving the list of exceptions on it. See [ProjectAccessState].
     */
    val access: ProjectAccessState? = null,
    /**
     * What **you** can do here, in one sentence — "You are a Viewer here, so you can
     * read this project and change nothing in it."
     *
     * Sent to everybody, at every rung, because it is a statement about the reader
     * rather than about anybody else. It is the whole of a Viewer's Access section and
     * the top line of an administrator's.
     */
    val yourAccessLine: String = "",
    /**
     * Whether this caller may delete the project, and [deleteBlockedReason] why not.
     *
     * The Owner's. The reason is sent for an Admin — the one rung that would
     * reasonably expect the button and does not get it — so the row can say which rung
     * it belongs to rather than simply not being there. Null for everybody else, who
     * are not offered the row at all: an explanation of a power three rungs up is
     * noise, not information.
     */
    val canDeleteProject: Boolean = false,
    val deleteBlockedReason: String? = null,
    /**
     * Whether the caller may change the project's **identity** — its name and its
     * prefix. The Owner's, like [canDeleteProject] and for a sharper reason than
     * "it is senior": a re-prefix rewrites what every ticket reference in every commit
     * message ever written about this project points at.
     *
     * Sent rather than inferred from [canConfigureRepository] (which is also the owner's,
     * and also true) because the two answer different questions and would have to be
     * un-conflated the day one of them moved.
     */
    val canMutateProjectIdentity: Boolean = false,
    val notifyOnNewIssue: Boolean = false,
    val canReceiveEmailNotifications: Boolean = false,
    /**
     * Whether this project offers a discussion forum and private messages
     * (LNL-96) — once the two switches a project administrator flipped in the
     * Features section here. Both features are retired (LNL-190): the section is
     * gone from the dialog, the server sends both as false for every project, and
     * these default false so a client talking to a server that omits them agrees.
     * The same two flags ride to the tab shell on [ProjectSummary].
     */
    val discussionsEnabled: Boolean = false,
    val messagesEnabled: Boolean = false,
    /**
     * Whether filing a new ticket must carry a label, and whether it must carry a
     * component (LNL-106) — the two switches a project administrator flips in the
     * Structure tab here. Both default off, the state every project had before the
     * switches existed. Sent to every caller who reaches this state, but only the
     * admin half renders the section that edits them; see [canMutateProject]. The
     * same two flags ride to the issue editor on [ProjectSummary], which is what
     * actually enforces the requirement in the editor.
     */
    val requireLabel: Boolean = false,
    val requireComponent: Boolean = false,
    /**
     * Whether closing an issue with a done resolution must carry a fixed version
     * (LNL-134). Default off. Rides to the board and resolution dialog on
     * [ProjectSummary], which is what actually enforces it at close time. See
     * [requireLabel].
     *
     * Rendered in the **Versions** section since LNL-196, not beside its two siblings
     * in Structure: a switch that cannot be satisfied without a version has no business
     * living two sections away from the list of them. It is still written through
     * [ProjectRequirements] with the other two, and is still an administrator's — which
     * is what makes it the one control in a Maintainer's Versions section that is dead.
     */
    val requireFixedVersionOnResolve: Boolean = false,
    /**
     * Whether the board shows each card's author on a muted footer line (LNL-157) —
     * a display toggle a project administrator flips, alongside the requirement
     * toggles here. Default off, the opt-in state every project had before the
     * column existed. Sent to every caller who reaches this state, but only the
     * admin half renders the section that edits it; see [canMutateProject]. The same
     * flag rides to the board on [ProjectSummary], which is what the card render reads.
     */
    val showIssueAuthor: Boolean = false,
    /**
     * Whether the board and its issue windows hide the issue number (LNL-194) — the
     * second board-display switch beside [showIssueAuthor], and sent to every caller
     * who reaches this state for its reason exactly: it is not a secret, the board
     * reads it to draw a card, and only Admin and above renders it as editable.
     */
    val hideIssueNumbers: Boolean = false,
    /**
     * The linked GitHub repository as `owner/name`, or empty because none is.
     *
     * **Admin only, and omitted rather than flagged.** The server sends this
     * empty string to every non-admin whatever the project has configured, on the
     * same principle as the vocabularies above: a field the caller may not see is
     * left out of the response, not sent and hidden by the browser. A secret that
     * reaches the client and is merely not rendered is not withheld — it is one
     * devtools panel away.
     *
     * What is admin-only is this *field* — the one the settings form edits — and
     * not the address itself, which LNL-178 put on [BoardState.gitHubRepository]
     * for every reader of the project: a `#123` in an issue is a link to that pull
     * request, and a link is a URL the client has to be able to build. The
     * distinction that survives is the one that was always doing the work: the
     * token never travels, to anybody. See [BoardState.gitHubRepository].
     */
    val repositoryUrl: String = "",
    /**
     * Whether the caller may see and set the repository fields below.
     *
     * Strictly narrower than [canMutateProject], and a sibling of
     * [canDeleteProject] in both shape and reason: an **owner or a system
     * administrator** (LNL-107), where the rest of the admin half opened up to
     * project administrators in LNL-37.
     *
     * The split is not tidiness. The repository is part of a project's *identity*
     * — the write goes through `canOwnProject`, the owner's gate — and, more
     * sharply for the token, the fields can carry a deployment secret. Sending
     * them to a project administrator would render them editable and then 403 on
     * save.
     *
     * When false the fields below are **empty regardless of what the project has
     * configured**, for the reason [repositoryUrl] gives: a value the caller may
     * not have does not travel and get hidden, it does not travel.
     */
    val canConfigureRepository: Boolean = false,
    /**
     * The name of the environment variable holding the GitHub token — never the
     * token. Read only when [githubTokenMode] is `env`; empty otherwise.
     *
     * Owner only, for [repositoryUrl]'s reason and more sharply: this names a
     * variable in the deployment, which is a hint about how the deployment is put
     * together and is nobody's business but an owner's.
     *
     * Constrained to a prefix on write; see the server's `parseTokenEnvName` for
     * why an owner may not name any variable they like.
     */
    val githubTokenEnv: String = "",
    /**
     * Which source the token comes from: `none`, `env`, or `literal` — the state
     * of the dialog's radio (LNL-107). See [TokenModes].
     *
     * The literal token itself is **never sent back**: a stored secret does not
     * travel to the browser, so there is no `githubTokenLiteral` here to mirror
     * [githubTokenEnv]. This mode is the whole of what the dialog learns about a
     * literal token — that one is configured — which is enough to select the radio
     * and show the field as a "leave blank to keep it" placeholder. Owner only,
     * `none` for everyone else, for [canConfigureRepository]'s reason.
     */
    val githubTokenMode: String = TokenModes.NONE,
) {
    /** The rows of one kind, so a caller with a [VocabularyKind] does not have to branch. */
    fun entriesFor(kind: VocabularyKind): List<VocabularyEntry> = when (kind) {
        VocabularyKind.LABEL -> labels
        VocabularyKind.COMPONENT -> components
        VocabularyKind.STATUS -> statuses
        VocabularyKind.PRIORITY -> priorities
        VocabularyKind.RESOLUTION -> resolutions
        VocabularyKind.SPRINT -> sprints
        VocabularyKind.VERSION -> versions
        VocabularyKind.RELATION_KIND -> relationKinds
    }
}

/** What the "add" field sends. The kind is in the path. */
@Serializable
data class VocabularyAdd(
    val name: String,
    /**
     * The to-side label, for a relation kind — or null for one that reads the same in
     * both directions (LNL-215). Ignored for every other kind, like the extras on
     * [VocabularyEdit].
     *
     * Accepted at ADD time, unlike a status's closing flag, which is only ever set by
     * the rename that follows. The settings dialog does add a bare name and then edit
     * the row, and the MCP tool creates a kind in ONE call — a kind whose opposite
     * could only be named by a second write would be briefly and visibly symmetric when
     * it is not, which is a wrong board state rather than an unfinished one.
     */
    val inverseName: String? = null,
    /**
     * The blocking flag, for a relation kind (LNL-215). Defaults false, and the default
     * is the decision: this switch is what greys cards on everybody's board, so arming
     * it is a deliberate act — exactly as a newly added status never demands a
     * resolution.
     */
    val marksBlocked: Boolean = false,
)

/**
 * "Switch this project's discussions and messages on or off" (LNL-96).
 *
 * Both flags together, never one at a time — the section renders two toggles but
 * they are one project's feature set, and the server takes the pair so a stale
 * client cannot resurrect the other by omitting it. Names the desired state
 * rather than "toggle", for [VocabularyEdit]'s reason: a retry says the same
 * thing, and two admins cannot flip a project's discussions back and forth by
 * both clicking once. Project administrator only, enforced at the route.
 */
@Serializable
data class ProjectFeatures(
    val discussionsEnabled: Boolean = true,
    val messagesEnabled: Boolean = true,
)

/**
 * "Switch this project's new-ticket requirements on or off" (LNL-106).
 *
 * Both flags together, for [ProjectFeatures]' reason exactly: the Structure tab
 * renders two toggles but they are one project's requirement set, and the server
 * takes the pair so a stale client cannot resurrect the other by omitting it.
 * Names the desired state rather than "toggle". Project administrator only,
 * enforced at the route.
 */
@Serializable
data class ProjectRequirements(
    val requireLabel: Boolean = false,
    val requireComponent: Boolean = false,
    /** Whether closing with a done resolution must carry a fixed version (LNL-134). */
    val requireFixedVersionOnResolve: Boolean = false,
)

/**
 * "This is how this project's board reads" (LNL-157, LNL-194).
 *
 * A parallel to [ProjectRequirements], its own request type because a display
 * choice is not a requirement — the two are set through different routes so a
 * stale client toggling one cannot resurrect the other. Names the desired state
 * rather than "toggle", for [ProjectRequirements]' reason.
 *
 * Both switches together, like [ProjectRequirements] and for its reason: the Board
 * display group renders the pair and sends the pair, so a stale client cannot
 * resurrect the other by omitting it.
 *
 * **Admin and above**, enforced at the route — they go up with the vocabulary, not
 * with the sprints. Everyone below sees them and cannot change them, which is
 * deliberate: hiding a switch that explains why the board looks the way it does only
 * prompts "where did the issue numbers go".
 */
@Serializable
data class ProjectDisplaySettings(
    /** Whether the board shows each card's author on a muted footer line. */
    val showIssueAuthor: Boolean = false,
    /**
     * Whether the board and its issue windows hide the issue number (LNL-194).
     *
     * A per-user preference until LNL-194 — "a choice for you alone" — and now the
     * project's, because a shared board reads one way for everybody looking at it.
     * The old per-user values were copied onto each project from its owner's; see the
     * server's copyBoardDisplayFromOwners.
     */
    val hideIssueNumbers: Boolean = false,
)

/**
 * What a rename sends.
 *
 * @property requiresResolution the closing flag, for a status. Ignored by the
 *   server for every other kind rather than refused: the dialog sends the row it
 *   is rendering, and a priority row simply has nothing to put here. Refusing
 *   would make the client responsible for knowing which kinds have the flag,
 *   which is the server's rule to keep.
 *
 *   Sent on every rename rather than as its own toggle route, because a status's
 *   name and its flag are one edit — see Statuses.sq's `update`.
 */
@Serializable
data class VocabularyEdit(
    val name: String,
    val requiresResolution: Boolean = false,
    /**
     * The done flag, for a resolution (LNL-134). Ignored by the server for every
     * other kind rather than refused, for [requiresResolution]'s reason exactly —
     * the dialog sends the row it is rendering. Sent on every rename because a
     * resolution's name and its done flag are one edit; see Resolutions.sq's `update`.
     */
    val isDone: Boolean = false,
    /**
     * The to-side label, for a relation kind (LNL-215). Ignored by the server for
     * every other kind rather than refused, for [requiresResolution]'s reason.
     *
     * **Null means symmetric** — the row's "same in both directions" checkbox sends
     * null, and there is nothing else to send. A blank string is normalised to null
     * server-side rather than stored, so "I cleared the field" and "I ticked the box"
     * cannot become two different stored states that render identically.
     */
    val inverseName: String? = null,
    /**
     * The blocking flag, for a relation kind (LNL-215). Ignored elsewhere, for
     * [requiresResolution]'s reason. Sent on every rename because a kind's names and
     * its flag are one edit; see IssueRelationKinds.sq's `update`.
     */
    val marksBlocked: Boolean = false,
    /**
     * The unblocking flag, for a status. Ignored elsewhere, for [requiresResolution]'s
     * reason, and sent on every rename with it — see Statuses.sq's `update`. Defaulted
     * false, so an older client's rename clears it: the same contract every flag here
     * has, since the dialog always sends the whole row.
     */
    val unblocks: Boolean = false,
)

/**
 * "This project estimates like this" (LNL-215).
 *
 * Its own request beside [ProjectDisplaySettings] and [ProjectRequirements] rather
 * than a field on either, for the reason those two are separate from each other: a
 * display choice is about how a board *reads*, a requirement is about what a ticket
 * must *carry*, and this is about what the editor *offers*. Three different kinds of
 * switch, three routes, so a stale client sending one cannot reset another in
 * passing.
 *
 * **Admin and above**, enforced at the route, with the vocabulary rather than with
 * the sprints — deciding whether a team estimates at all is a decision about what the
 * board is.
 *
 * @property mode one of [EstimateMode]'s keys. An unrecognised value is folded to
 *   `none` rather than refused, for [EstimateMode.fromKey]'s reason.
 */
@Serializable
data class ProjectEstimateSettings(
    val mode: String = "none",
)

/**
 * A whole vocabulary, in the order the admin just put it in.
 *
 * The whole list, not "move row 3 up one" — [IssueOrderUpdate]'s reasoning, for
 * the same reason: a position is a statement about neighbours, and a server told
 * only about the row that moved would have to reconstruct the rest and guess. The
 * client already knows the order; it is what it just rendered.
 *
 * The server checks that these are exactly this vocabulary's ids — all of them,
 * none repeated, nothing foreign — before it writes. A partial list would leave
 * the rows it omitted holding positions that now collide with the ones it named.
 *
 * @property ids every row of one kind, first to last.
 */
@Serializable
data class VocabularyOrder(
    val ids: List<Long> = emptyList(),
)

// `RoleGrant` — a user, a role key and a granted flag — stood here and is gone
// (LNL-194). A person holds one rung per project, so the tick-box shape had to guess
// what unticking one box among five meant; [RungGrant] names the rung to move to, and
// null is "no access".
